import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CreditCard, Download, FileText, Loader2 } from 'lucide-react';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BILLING_OVERVIEW_KEY, billingApi } from '@/lib/billing-api';
import { getErrorMessage } from '@/lib/errors';
import { useSubscriptionAccess } from '@/hooks/use-subscription-access';
import { hasLapsed, periodSummary } from '@/lib/subscription-display';
import { daysLeftPhrase, longDate, trialEndsPhrase } from '@/lib/trial';
import { ChoosePlanPanel } from '@/modules/billing/ChoosePlanPanel';
import { PaymentMethodCard } from '@/modules/billing/PaymentMethodCard';
import { SubscriptionNotice } from '@/modules/billing/SubscriptionNotice';
import type {
  GatewayOption,
  Invoice,
  InvoiceStatus,
  Subscription,
  SubscriptionStatus,
} from '@/types/api';

const subscriptionTone: Record<SubscriptionStatus, 'success' | 'warning' | 'danger' | 'muted'> = {
  active: 'success',
  trialing: 'success',
  past_due: 'warning',
  incomplete: 'muted',
  suspended: 'danger',
  canceled: 'muted',
  expired: 'muted',
  // Filtered out server-side before it reaches this page; mapped so the type stays exhaustive.
  abandoned: 'muted',
};

const invoiceTone: Record<InvoiceStatus, 'success' | 'warning' | 'danger' | 'muted'> = {
  paid: 'success',
  open: 'warning',
  draft: 'muted',
  void: 'muted',
  uncollectible: 'danger',
};

export function BillingPage() {
  const queryClient = useQueryClient();
  const { isStaff } = useSubscriptionAccess();
  const overview = useQuery({ queryKey: BILLING_OVERVIEW_KEY, queryFn: billingApi.overview });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: BILLING_OVERVIEW_KEY });

  const cancel = useMutation({
    mutationFn: (id: string) => billingApi.cancelSubscription(id, 'Cancelled from the billing page'),
    onSuccess: invalidate,
  });
  // Renewing sends the customer to the gateway rather than changing anything here: for a
  // customer, renewal means paying. The free version is an administrator's tool.
  const renew = useMutation({
    mutationFn: ({ id, gateway }: { id: string; gateway: string }) =>
      billingApi.renewSubscription(id, gateway),
    onSuccess: (session) => {
      window.location.href = session.checkout_url;
    },
  });

  const resume = useMutation({
    mutationFn: (id: string) => billingApi.resumeSubscription(id),
    onSuccess: invalidate,
  });

  const download = useMutation({
    mutationFn: (invoice: Invoice) => billingApi.downloadInvoice(invoice.id, invoice.number),
  });

  const busy = cancel.isPending || resume.isPending || renew.isPending;
  const actionError = cancel.error ?? resume.error ?? renew.error ?? download.error;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Billing</h1>
        <p className="text-sm text-muted-foreground">
          Your subscriptions, invoices, and payment history.
        </p>
      </div>

      {/* The same notice the Servers page shows. Billing is where a customer comes to fix a
          lapsed subscription, so it is the one page that must state the problem and the remedy
          rather than leaving a status badge to imply them. */}
      <SubscriptionNotice />

      {actionError && <Alert variant="danger">{getErrorMessage(actionError)}</Alert>}
      {overview.isError && <Alert variant="danger">{getErrorMessage(overview.error)}</Alert>}

      {overview.isLoading && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Loading billing…
        </p>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Subscriptions</CardTitle>
          <CardDescription>
            Cancelling keeps your access until the end of the period you have paid for. First-time
            customers can request a refund of their first subscription payment within 7 days
            through support; cancelling on its own does not refund a period already paid for.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {overview.data?.subscriptions.length === 0 && (
            <div className="flex flex-col items-center gap-2 py-8 text-center">
              <CreditCard className="size-8 text-muted-foreground" />
              {/* Staff operate the platform rather than subscribe to it, so "choose a plan" is
                  not advice they can act on — the same reason SubscriptionNotice hides from
                  them and the gate exempts them. */}
              <p className="font-medium">
                {isStaff ? 'Platform staff hold no subscription' : 'No active subscription'}
              </p>
              <p className="text-sm text-muted-foreground">
                {isStaff
                  ? 'Your access is not metered. Customer subscriptions are managed under Administration.'
                  : overview.data?.trial.can_start === true
                    ? 'Start the free trial above, or choose a plan below.'
                    : 'Choose a plan to get started.'}
              </p>
            </div>
          )}

          {overview.data?.subscriptions.map((subscription) => (
            <SubscriptionRow
              key={subscription.id}
              subscription={subscription}
              busy={busy}
              onCancel={() => cancel.mutate(subscription.id)}
              onResume={() => resume.mutate(subscription.id)}
              gateways={overview.data?.available_gateways ?? []}
              onRenew={(gateway) => renew.mutate({ id: subscription.id, gateway })}
            />
          ))}
        </CardContent>
      </Card>

      {/* Only where there is something to manage. A customer with no gateway-backed subscription
          would get an error on click, which is a worse answer than not offering it. */}
      {!isStaff &&
        overview.data?.subscriptions.some((s) => s.grants_access && s.gateway !== null) === true && (
          <PaymentMethodCard />
        )}

      {/* Staff operate the platform rather than subscribe to it, so a plan picker is not advice
          they can act on — the same reason the empty state above speaks to them differently. */}
      {!isStaff && overview.data !== undefined && (
        <ChoosePlanPanel
          gateways={overview.data.available_gateways}
          // The trial is not a plan to change from: buying one ends it rather than running beside it.
          hasSubscription={overview.data.subscriptions.some((s) => !s.free_trial && !hasLapsed(s))}
          onTrial={overview.data.trial.status === 'running'}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>Invoices</CardTitle>
          <CardDescription>Download a PDF copy of any issued invoice.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {overview.data?.invoices.length === 0 && (
            <p className="text-sm text-muted-foreground">No invoices yet.</p>
          )}

          {overview.data?.invoices.map((invoice) => (
            <InvoiceRow
              key={invoice.id}
              invoice={invoice}
              onDownload={() => download.mutate(invoice)}
              busy={download.isPending}
            />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * The free trial's own row.
 *
 * Not the ordinary row with some buttons hidden: a trial has no price to show, nothing to cancel and
 * nothing to renew — it ends by itself, and the API refuses all three for one. "USD 0.00 · Monthly"
 * with a Cancel button described a subscription this is not.
 */
function FreeTrialRow({ subscription }: { subscription: Subscription }) {
  const running = subscription.trial_days_left !== null && subscription.current_period_end !== null;

  const detail = running
    ? `${daysLeftPhrase(subscription.trial_days_left ?? 0)} · ${trialEndsPhrase(subscription.current_period_end ?? '')}`
    : // Ended early (a plan started, or support ended it) or ran its course.
      subscription.canceled_at !== null
      ? `Ended ${longDate(subscription.canceled_at)}`
      : subscription.current_period_end !== null
        ? `Ended ${longDate(subscription.current_period_end)}`
        : 'Ended';

  return (
    <div className="flex flex-col gap-3 rounded-md border border-border bg-muted/20 px-4 py-3 sm:flex-row sm:items-center">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">Free trial</p>
        <p className="text-xs text-muted-foreground">
          {detail}
          {running && ' · No card on file and nothing to cancel'}
        </p>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <Badge variant={running ? 'success' : 'muted'}>{running ? 'Running' : 'Ended'}</Badge>
      </div>
    </div>
  );
}

function SubscriptionRow({
  subscription,
  busy,
  onCancel,
  onResume,
  gateways,
  onRenew,
}: {
  subscription: Subscription;
  busy: boolean;
  onCancel: () => void;
  onResume: () => void;
  gateways: GatewayOption[];
  onRenew: (gateway: string) => void;
}) {
  if (subscription.free_trial) {
    return <FreeTrialRow subscription={subscription} />;
  }

  const lapsed = hasLapsed(subscription);

  // Suspension is deliberately excluded: it is an administrative hold, and paying does not
  // lift it. Offering "Renew now" there would take money and change nothing.
  // Not while the provider is still renewing it: past due with access left means the provider is
  // retrying the card, and a second checkout here would start a second subscription beside the
  // one being renewed. Updating the payment method (below) is what fixes that.
  const providerRetrying = subscription.renews_automatically && subscription.grants_access;

  const canRenew =
    subscription.billing_interval !== 'lifetime' &&
    subscription.effective_status !== 'suspended' &&
    !providerRetrying &&
    (lapsed ||
      subscription.effective_status === 'expired' ||
      subscription.effective_status === 'past_due');

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-muted/20 px-4 py-3">
      <div className="min-w-0">
        <p className="text-sm font-medium">
          {subscription.currency} {subscription.price_formatted} ·{' '}
          {subscription.billing_interval_label}
        </p>
        <p className="text-xs text-muted-foreground">
          {periodSummary(subscription)}
          {subscription.in_trial && subscription.trial_ends_at &&
            ` · trial until ${new Date(subscription.trial_ends_at).toLocaleDateString()}`}
          {subscription.gateway && ` · via ${subscription.gateway.replace(/_/g, ' ')}`}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={subscriptionTone[subscription.effective_status]}>
          {subscription.effective_status_label}
        </Badge>
        {subscription.cancel_at_period_end && <Badge variant="warning">Cancelling</Badge>}

        {subscription.cancel_at_period_end ? (
          <Button variant="ghost" size="sm" onClick={onResume} disabled={busy}>
            Keep subscription
          </Button>
        ) : (
          subscription.grants_access && (
            <Button variant="ghost" size="sm" className="text-danger" onClick={onCancel} disabled={busy}>
              Cancel
            </Button>
          )
        )}

        {/* Renewal is offered exactly when it would help: a lapsed or lapsing subscription on a
            recurring plan. Lifetime has nothing to renew, a suspension cannot be paid off, and
            a healthy subscription renews itself. */}
        {canRenew && gateways.length > 0 && (
          <Button size="sm" onClick={() => onRenew(gateways[0]!.identifier)} disabled={busy}>
            {busy ? <Loader2 className="animate-spin" /> : null} Renew now
          </Button>
        )}
        {canRenew && gateways.length === 0 && (
          <span className="text-xs text-muted-foreground">
            No payment method is configured. Contact support to renew.
          </span>
        )}
      </div>
    </div>
  );
}

function InvoiceRow({
  invoice,
  onDownload,
  busy,
}: {
  invoice: Invoice;
  onDownload: () => void;
  busy: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-muted/20 px-4 py-3">
      <div className="flex min-w-0 items-start gap-3">
        <FileText className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0">
          <p className="font-mono text-sm">{invoice.number}</p>
          <p className="text-xs text-muted-foreground">
            {invoice.currency} {invoice.total_formatted}
            {invoice.issued_at && ` · issued ${new Date(invoice.issued_at).toLocaleDateString()}`}
            {invoice.status === 'open' && invoice.due_at &&
              ` · due ${new Date(invoice.due_at).toLocaleDateString()}`}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {invoice.overdue && <Badge variant="danger">Overdue</Badge>}
        <Badge variant={invoiceTone[invoice.status]}>{invoice.status_label}</Badge>
        <Button variant="ghost" size="sm" onClick={onDownload} disabled={busy}>
          {busy ? <Loader2 className="animate-spin" /> : <Download />} PDF
        </Button>
      </div>
    </div>
  );
}
